/**
 * UFVK + UA derivation from the user's BIP-39 mnemonic.
 *
 * Two WASM modules are involved at different moments:
 *
 *   `@chainsafe/webzjs-keys`      — ZIP-32 derivation: seed → USK → UFVK.
 *                                   The UFVK is the only thing the
 *                                   scanner backend ever sees (HPKE-sealed).
 *
 *   `leokit-shielded-sign`        — exposes derive_default_unified_address
 *                                   so we can show the user their u1… UA
 *                                   without a backend round-trip.
 *
 * Both modules are loaded LAZILY (dynamic import) — the boot path doesn't
 * pay their WASM compile cost, only the wallet-create / restore flow
 * does, and only once per session (each module caches its init Promise).
 */

import { mnemonicToSeedSync } from "@scure/bip39";

export interface ShieldedKeys {
  /** ZIP-316 UFVK encoded string, e.g. "uview1..." */
  ufvk: string;
  /** Default Unified Address (u1...) */
  unifiedAddress: string;
  /** ZIP-32 account index used to derive both. */
  account: number;
}

let webzjsInitPromise: Promise<typeof import("@chainsafe/webzjs-keys")> | null = null;
let signerInitPromise: Promise<typeof import("leokit-shielded-sign")> | null = null;

async function loadWebzjsKeys(): Promise<typeof import("@chainsafe/webzjs-keys")> {
  if (!webzjsInitPromise) {
    webzjsInitPromise = (async () => {
      const mod = await import("@chainsafe/webzjs-keys");
      const init = (mod as { default?: () => Promise<unknown> }).default;
      if (typeof init === "function") {
        await init();
      }
      return mod;
    })();
  }
  return webzjsInitPromise;
}

async function loadSigner(): Promise<typeof import("leokit-shielded-sign")> {
  if (!signerInitPromise) {
    signerInitPromise = (async () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const mod = (await import("leokit-shielded-sign" as any)) as typeof import("leokit-shielded-sign");
      const init = (mod as { default?: () => Promise<unknown> }).default;
      if (typeof init === "function") {
        await init();
      }
      return mod;
    })();
  }
  return signerInitPromise;
}

/**
 * Derive UFVK + default Unified Address from the user's mnemonic.
 *
 * Inputs:
 *   - phrase: trimmed/normalised BIP-39 mnemonic (12 or 24 words)
 *   - account: ZIP-32 account index (default 0)
 *
 * Output: the UFVK encoded string (what the scanner sees, HPKE-sealed
 * on every request), plus the canonical u1… UA the user shares.
 *
 * Throws if either WASM module fails to load, or if the mnemonic
 * couldn't be reduced to a valid seed (caller should pre-validate
 * via wallet/crypto/mnemonic.ts).
 */
export async function deriveShieldedKeysFromMnemonic(
  phrase: string,
  account: number = 0,
): Promise<ShieldedKeys> {
  // BIP-39 seed (64 bytes) — webzjs accepts ≥ 32 bytes; we feed the
  // full thing for compatibility with other Sapling wallets.
  const seed = mnemonicToSeedSync(phrase);

  const webzjs = await loadWebzjsKeys();
  const signer = await loadSigner();

  // USK → UFVK encoded string. Mainnet only for now.
  const usk = new webzjs.UnifiedSpendingKey("main", seed, account);
  try {
    const ufvkObj = usk.to_unified_full_viewing_key();
    const ufvk = ufvkObj.encode("main");

    // Default UA — direct derive_default_unified_address in the signer
    // WASM. No backend, works offline, byte-equal to what the backend
    // /wallet/address would return.
    const unifiedAddress = signer.derive_default_unified_address(phrase, account);

    return { ufvk, unifiedAddress, account };
  } finally {
    // Best-effort scrubbing — wasm-bindgen lets us release the USK's
    // internal heap explicitly so the spending material doesn't sit
    // around longer than needed.
    if (typeof usk.free === "function") {
      try {
        usk.free();
      } catch {
        /* ignore */
      }
    }
    seed.fill(0);
  }
}
